import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, BookOpen, RotateCcw } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  MISSED_DECK_META,
  STARTER_DECKS,
  type Deck,
} from "@/learn/decks";
import {
  loadProgress,
  summarize,
  type DeckSummary,
} from "@/learn/leitner";
import {
  clearMissedCards,
  loadMissedCards,
} from "@/learn/missedFromPlay";
import Main from "@/components/Main";

/**
 * Learn hub - lists every deck with its "due today" count and overall
 * mastery. Quiet, calm visual treatment to contrast with the Play tab.
 *
 * Routing: /learn -> this page. Per-deck study at /learn/:deckId.
 */
export default function LearnHub() {
  const [missedCards, setMissedCards] = useState(() => loadMissedCards());
  const [, refreshTick] = useState(0);

  // Re-read progress on mount so coming back from a session shows fresh
  // due counts.
  useEffect(() => {
    refreshTick((t) => t + 1);
  }, []);

  const missedDeck: Deck = useMemo(
    () => ({ ...MISSED_DECK_META, cards: missedCards }),
    [missedCards],
  );

  const allDecks = useMemo<Deck[]>(
    () => [...STARTER_DECKS, missedDeck],
    [missedDeck],
  );

  const summaries = useMemo(() => {
    const out = new Map<string, DeckSummary>();
    for (const d of allDecks) {
      const progress = loadProgress(d.id);
      out.set(d.id, summarize(d.cards.map((c) => c.id), progress));
    }
    return out;
  }, [allDecks]);

  const totalDue = useMemo(
    () => [...summaries.values()].reduce((s, x) => s + x.due, 0),
    [summaries],
  );

  return (
    <div className="min-h-screen bg-transparent">
      <AppHeader />
      <Main className="max-w-[900px] mx-auto px-4 py-6">
        <header className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen size={18} className="text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-bold text-foreground">Learn</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Spaced-repetition flashcards for the facts you need cold.
              Quiet practice - no clock, no scoreboard.
            </p>
          </div>
          {totalDue > 0 && (
            <Badge variant="secondary" className="shrink-0 mt-1">
              {totalDue} due today
            </Badge>
          )}
        </header>

        <section aria-label="Starter decks" className="grid gap-3 sm:grid-cols-2">
          {STARTER_DECKS.map((d) => (
            <DeckCard
              key={d.id}
              deck={d}
              summary={summaries.get(d.id)!}
            />
          ))}
        </section>

        <section aria-label="From your games" className="mt-8">
          <h2 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            <RotateCcw size={14} className="text-primary" aria-hidden="true" />
            From your games
          </h2>
          {missedCards.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Play Element Battle or Electron Config Sprint - elements you
              miss appear here as a personalised review deck.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <DeckCard
                deck={missedDeck}
                summary={summaries.get(missedDeck.id)!}
                action={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[11px] h-7 text-muted-foreground"
                    onClick={() => {
                      if (window.confirm("Clear all missed-from-play cards?")) {
                        clearMissedCards();
                        setMissedCards([]);
                      }
                    }}
                  >
                    Clear
                  </Button>
                }
              />
            </div>
          )}
        </section>

        <p className="text-[11px] text-muted-foreground mt-8">
          Progress is stored in this browser only. No accounts, no sync.
        </p>
      </Main>
    </div>
  );
}

function DeckCard({
  deck,
  summary,
  action,
}: {
  deck: Deck;
  summary: DeckSummary;
  action?: React.ReactNode;
}) {
  const Icon = deck.icon;
  const masteryPct = summary.total
    ? Math.round((summary.mastered / summary.total) * 100)
    : 0;
  const disabled = summary.total === 0;

  return (
    <article
      className={`rounded-xl border border-border bg-card p-4 flex flex-col gap-3 ${
        disabled ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Icon size={14} className="text-primary" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-foreground truncate">
              {deck.title}
            </h3>
          </div>
          <p className="text-xs text-muted-foreground leading-snug">
            {deck.description}
          </p>
        </div>
        {summary.due > 0 && (
          <Badge variant="default" className="shrink-0 text-[10px]">
            {summary.due} due
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="font-mono tabular-nums">{summary.total}</span>
        <span>cards</span>
        <span aria-hidden="true">·</span>
        <span className="font-mono tabular-nums">{summary.mastered}</span>
        <span>mastered</span>
        <span className="ml-auto font-mono tabular-nums">{masteryPct}%</span>
      </div>
      <div
        className="h-1.5 rounded-full bg-muted overflow-hidden"
        role="progressbar"
        aria-valuenow={masteryPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${deck.title} mastery: ${masteryPct} percent`}
      >
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${masteryPct}%` }}
        />
      </div>

      <div className="flex items-center gap-2 mt-1">
        {disabled ? (
          <Button size="sm" variant="outline" disabled className="text-xs h-8">
            No cards yet
          </Button>
        ) : (
          <Button asChild size="sm" className="text-xs h-8">
            <Link to={`/learn/${deck.id}`}>
              Study
              <ArrowRight size={12} className="ml-1" aria-hidden="true" />
            </Link>
          </Button>
        )}
        {action}
      </div>
    </article>
  );
}